import React from "react";
import { ArrowRight, CheckCircle2, Info, CalendarClock, Handshake, Search, UserCheck, FileSearch, Scale, FileSignature, Package, Award } from "lucide-react";
import { ICONS, SectionHead, StatusPill } from "./acqUi";

export function Qualify({ items, text, goTo }) {
  return (
    <section data-testid="acq-qualify-section" id="acq-requirements">
      <SectionHead eyebrow="Eligibility" title="What You Need to Qualify" text={text} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((q, i) => {
          const Icon = ICONS[q.icon] || Info;
          return (
            <div key={q.key} className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col" data-testid={`acq-qual-${q.key}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-full bg-[#0B2E4F] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <Icon className="w-5 h-5 text-[#006090]" />
                </span>
                <StatusPill p={q.progress} testId={`acq-qual-status-${q.key}`} />
              </div>
              <h3 className="mt-3 font-semibold text-slate-900">{q.title}</h3>
              <p className="mt-1 text-sm text-slate-600 leading-snug flex-1">{q.text}</p>
              {q.link && <button onClick={() => goTo(q.link.tab, q.link.sub)} className="mt-3 self-start text-sm font-medium text-[#006090] inline-flex items-center gap-1 hover:gap-1.5 transition-[gap]" data-testid={`acq-qual-link-${q.key}`}>{q.link.label} <ArrowRight className="w-3.5 h-3.5" /></button>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function Timeline({ steps, note, text }) {
  return (
    <section data-testid="acq-timeline-section">
      <SectionHead eyebrow="Your Path to Acquisition" title="Typical Acquisition Timeline" text={text} />
      <ol className="relative grid md:grid-cols-3 gap-6 md:gap-4">
        <span className="hidden md:block absolute top-6 left-[8%] right-[8%] h-0.5 bg-gradient-to-r from-[#006090] to-[#0D9488]" aria-hidden />
        <span className="md:hidden absolute top-2 bottom-2 left-6 w-0.5 bg-[#006090]/30" aria-hidden />
        {steps.map((s) => (
          <li key={s.key} className="relative flex md:flex-col gap-4 md:gap-0" data-testid={`acq-timeline-${s.key}`}>
            <span className="relative z-10 shrink-0 w-12 h-12 rounded-full bg-white border-2 border-[#006090] text-[#006090] flex items-center justify-center md:mx-auto"><CalendarClock className="w-5 h-5" /></span>
            <div className="md:mt-4 flex-1 rounded-xl border border-slate-200 bg-white p-5">
              <p className="font-display text-xl font-bold text-slate-900">{s.label}</p>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">{s.sub}</p>
              <ul className="mt-3 space-y-1.5">{s.items.map((x) => <li key={x} className="text-sm text-slate-700 flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#0D9488] shrink-0 mt-0.5" />{x}</li>)}</ul>
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-3 text-xs text-slate-500 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{note}</p>
    </section>
  );
}

const FIND = [
  { Icon: UserCheck, t: "Become qualified and approved" }, { Icon: Search, t: "Review potential acquisition opportunities" },
  { Icon: Handshake, t: "Meet the selling agency owner" }, { Icon: FileSearch, t: "Evaluate the agency opportunity" },
  { Icon: Scale, t: "Negotiate purchase terms" }, { Icon: FileSignature, t: "Execute the purchase agreement" },
  { Icon: Package, t: "Prepare the transition" }, { Icon: Award, t: "Complete Farmers approval and transfer" },
];

export function FindingAgency({ text, image }) {
  return (
    <section data-testid="acq-finding-section" className="grid lg:grid-cols-[1fr_1.1fr] gap-6 items-stretch">
      <div className="relative rounded-2xl overflow-hidden min-h-[220px]">
        <img src={image} alt="Agency owners meeting" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B2E4F]/90 via-[#0B2E4F]/30 to-transparent" />
        <div className="absolute bottom-0 p-6 text-white">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-200">Finding the Right Agency</p>
          <p className="font-display text-2xl font-bold mt-1">A private transaction between two business owners</p>
          <p className="text-sm text-white/85 mt-2">Farmers does not set the purchase price. The buying and selling parties negotiate the transaction terms.</p>
        </div>
      </div>
      <div>
        <p className="text-sm text-slate-600 mb-4">{text}</p>
        <ol className="grid sm:grid-cols-2 gap-2">
          {FIND.map(({ Icon, t }, i) => (
            <li key={t} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid={`acq-find-step-${i + 1}`}>
              <span className="text-xs font-bold text-[#006090] w-5 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
              <Icon className="w-4 h-4 text-slate-500 shrink-0" /><span className="text-sm text-slate-800">{t}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
